<template>
  <div class="captcha" data-testid="captcha-challenge">
    <label
      v-if="showLabel"
      id="captcha-label"
      class="captcha-label"
      for="captcha-container"
    >
      Security check *
    </label>
    <p id="captcha-help" class="captcha-help">
      Please complete the security check below so we know you are not a robot.
      Your referral is submitted once the check is complete.
    </p>

    <div
      id="captcha-container"
      ref="container"
      v-show="status !== 'verified'"
      class="captcha-container"
      role="group"
      :aria-labelledby="groupLabelId"
      aria-describedby="captcha-help captcha-status"
      :aria-busy="status === 'loading' ? 'true' : 'false'"
    ></div>

    <p
      v-if="status === 'loading'"
      id="captcha-status"
      class="captcha-status"
      role="status"
      aria-live="polite"
    >
      Loading the security check...
    </p>

    <p
      v-else-if="status === 'verified'"
      id="captcha-status"
      class="captcha-status captcha-status-success"
      role="status"
      aria-live="polite"
    >
      Security check complete. You can now submit your referral.
    </p>

    <div
      v-else-if="status === 'expired' || status === 'error'"
      id="captcha-status"
      class="captcha-status captcha-status-error"
      role="alert"
      aria-live="assertive"
    >
      <p class="captcha-message">{{ message }}</p>
      <button type="button" class="captcha-retry" @click="retry">
        Try the security check again
      </button>
    </div>

    <p
      v-else
      id="captcha-status"
      class="captcha-status"
      role="status"
      aria-live="polite"
    >
      Complete the security check to continue.
    </p>
  </div>
</template>

<script>
import {
  getCaptchaConfig,
  isCaptchaConfigured,
  renderCaptcha,
} from "../services/captcha";

const MESSAGES = {
  error:
    "We could not load the security check. Please check your internet " +
    "connection and try again.",
  failed:
    "The security check was not completed. Please try again.",
  expired:
    "Your security check has expired. Please complete it again before " +
    "submitting your referral.",
};

export default {
  name: "CaptchaChallenge",
  props: {
    // The modal supplies its own dialog heading, so the inline label and its
    // required marker are only needed when the check sits inside the form.
    showLabel: {
      type: Boolean,
      default: true,
    },
    labelledBy: {
      type: String,
      default: null,
    },
  },
  emits: ["verified", "unverified"],
  data() {
    return {
      // idle | loading | ready | verified | expired | error
      status: "loading",
      message: "",
      expiryTimer: null,
    };
  },
  computed: {
    groupLabelId() {
      if (this.showLabel) {
        return "captcha-label";
      }
      return this.labelledBy || undefined;
    },
  },
  mounted() {
    if (!isCaptchaConfigured()) {
      // The security check is not switched on for this environment, so the
      // form must remain usable.
      this.status = "verified";
      this.$emit("verified");
      return;
    }

    this.render();
  },
  beforeUnmount() {
    this.clearExpiryTimer();
  },
  methods: {
    async render() {
      this.status = "loading";
      this.message = "";
      this.clearExpiryTimer();
      this.teardownWidget();
      this.$emit("unverified");

      try {
        await renderCaptcha(this.$refs.container, {
          onLoad: this.handleLoad,
          onSuccess: this.handleSuccess,
          onError: this.handleError,
        });
      } catch (error) {
        this.handleError(error);
      }
    },

    handleLoad() {
      if (this.status === "loading") {
        this.status = "ready";
      }
    },

    handleSuccess() {
      // The AWS widget replaces the puzzle with its own success screen, which
      // says the user will be redirected shortly. That is only true for the
      // WAF interstitial, so remove the widget and use our own confirmation.
      this.teardownWidget();
      this.status = "verified";
      this.message = "";
      this.$emit("verified");
      this.startExpiryTimer();
    },

    teardownWidget() {
      const container = this.$refs.container;
      if (container) {
        container.innerHTML = "";
      }
    },

    handleError() {
      this.clearExpiryTimer();
      this.teardownWidget();
      this.status = "error";
      this.message = MESSAGES.error;
      this.$emit("unverified");
    },

    startExpiryTimer() {
      this.clearExpiryTimer();
      const { tokenLifetimeSeconds } = getCaptchaConfig();
      this.expiryTimer = setTimeout(() => {
        this.expire();
      }, tokenLifetimeSeconds * 1000);
    },

    clearExpiryTimer() {
      if (this.expiryTimer) {
        clearTimeout(this.expiryTimer);
        this.expiryTimer = null;
      }
    },

    expire(reason = "expired") {
      this.clearExpiryTimer();
      this.status = "expired";
      this.message = MESSAGES[reason] || MESSAGES.expired;
      this.$emit("unverified");
    },

    /** Called by the parent form when the API rejects the security check. */
    reset(reason = "failed") {
      if (!isCaptchaConfigured()) {
        return;
      }
      this.expire(reason);
      this.render();
    },

    retry() {
      this.render();
      this.$nextTick(() => {
        const focusable = this.$refs.container?.querySelector(
          "button, input, iframe, [tabindex]"
        );
        if (focusable) {
          focusable.focus();
        }
      });
    },
  },
};
</script>

<style scoped>
.captcha {
  margin: 10px 0 20px 0;
}

.captcha-label {
  display: block;
  font-size: 18px;
  font-weight: 600;
  color: #001f5a;
  margin-bottom: 5px;
}

.captcha-help {
  margin: 0 0 10px 0;
  font-size: 16px;
}

.captcha-container {
  min-height: 60px;
}

.captcha-status {
  margin: 10px 0 0 0;
  font-size: 16px;
}

.captcha-status-success {
  color: #14663d;
}

.captcha-status-error {
  color: #a4262c;
}

.captcha-message {
  margin: 0 0 10px 0;
}

.captcha-retry {
  cursor: pointer;
}
</style>
